import type { ClipboardEvent, DragEvent } from 'react'
import { PageHeader } from '@/components/layout/page-header'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  FileText,
  ImagePlus,
  Loader2,
  PlusCircle,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EnumSelect } from '@/components/ui/enum-select'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldHelp,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { apiClient } from '@/lib/api/client'
import {
  allCustomerChoices,
  customerFromExactEmail,
  customerIdentityLabel,
} from '@/lib/customer-identity'
import { ErrorState } from '@/src/components/ErrorState'
import { HintedLabel } from '@/src/components/FieldHint'
import { FIELD_HELP } from '@/src/lib/onboarding/field-help'
import { LoadingState } from '@/src/components/LoadingState'
import { CustomerCombobox } from '@/src/components/CustomerCombobox'
import { FormWizard, ReviewRow, ReviewSection } from '@/src/components/wizard/FormWizard'
import {
  TextAreaField,
  TextField,
  formatLabel,
  isEmail,
  isLink,
} from '@/src/components/wizard/fields'
import { useDraftState, useFormDraft } from '@/src/components/wizard/useFormDraft'
import {
  useFormWizard,
  type FieldErrors,
  type WizardStep,
} from '@/src/components/wizard/useFormWizard'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { isCustomerLogin } from '@/src/lib/roles'
import {
  IncotermSelect,
  LocationPicker,
  incotermModeHint,
  locationModeHint,
} from '@/src/components/LocationPicker'
import {
  CHINA,
  KAMPALA,
  incotermFromText,
  loadLocations,
  resolveLocation,
  typedChoice,
  type LocationChoice,
} from '@/lib/locations'
import { uploadInvoiceAsset } from '@/lib/storage/images'
import { defaultInquiryNextAction, inquiryNextActionsForStatus } from '@/lib/inquiry-workflow'
import type {
  CreateInquiryRequest,
  HelmAirService,
  HelmCommunicationChannel,
  HelmInquiryPriority,
  HelmInquiryStatus,
  HelmServiceType,
  InquiryDocumentArtifact,
} from '@/lib/api/types'
import {
  SHIPMENT_CHOICES,
  shipmentChoice,
  shipmentFields,
  type ShipmentChoice,
} from '@/src/lib/air-service'

const serviceTypes: HelmServiceType[] = [
  'PRODUCT_SOURCING',
  'SHIPPING_ONLY',
  'PVOC_TESTING',
  'CUSTOMS_CLEARANCE',
  'END_TO_END_IMPORT',
]
const priorities: HelmInquiryPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT']
const statuses: HelmInquiryStatus[] = ['DRAFT', 'SUBMITTED']
const channels: HelmCommunicationChannel[] = ['EMAIL', 'WHATSAPP', 'BOTH', 'NONE']
const inquirySources = ['WHATSAPP', 'INSTAGRAM', 'WEBSITE', 'EMAIL', 'WALK_IN', 'OTHER'] as const
type InquirySource = (typeof inquirySources)[number]
type ShippingMethod = 'AIR' | 'SEA' | 'ROAD' | 'NONE'

const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: formatLabel(value) }))

type StepId = 'source' | 'client' | 'products' | 'shipping' | 'review'

const steps: WizardStep<StepId>[] = [
  {
    id: 'source',
    title: 'Source',
    description: 'Where the request came from. A screenshot can fill in most of the rest.',
    optional: true,
  },
  {
    id: 'client',
    title: 'Client & services',
    description: 'Who is asking, and what they need from Xtellar.',
  },
  {
    id: 'products',
    title: 'Products',
    description: 'What they want, and what it is worth.',
  },
  {
    id: 'shipping',
    title: 'Shipping & supplier',
    description: 'How it travels and who supplies it, when that is already known.',
    optional: true,
  },
  {
    id: 'review',
    title: 'Follow-up & review',
    description: 'Decide what happens next, then check everything before creating the inquiry.',
  },
]

function centsFromInput(value: string) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : null
}

interface ProductDraft {
  name: string
  description: string
  quantity: number
  unit_price: string
  currency: string
  sourcing_url: string
  hs_code: string
  notes: string
}

const blankProduct = (): ProductDraft => ({
  name: '',
  description: '',
  quantity: 1,
  unit_price: '',
  currency: 'USD',
  sourcing_url: '',
  hs_code: '',
  notes: '',
})

const ACCEPTED_SOURCE = /^(image\/|application\/pdf$)/

export function NewInquiryPage() {
  const navigate = useNavigate()
  const draft = useFormDraft('new-inquiry')
  const {
    data: customers,
    loading,
    error,
    reload,
  } = useAsyncData(
    () => allCustomerChoices((page) => apiClient.listCustomersPage(page, { limit: 100 })),
    [],
  )
  // Priority and the due date are Xtellar's to set, so a customer filing their own
  // inquiry is not offered them.
  const profile = useAsyncData(() => apiClient.getProfile().catch(() => null), [])
  const customerLogin = isCustomerLogin(profile.data)
  const [customerId, setCustomerId] = useDraftState(draft, 'customerId', '')
  const [customerMode, setCustomerMode] = useDraftState<'existing' | 'provisional'>(
    draft,
    'customerMode',
    'existing',
  )
  const [provisionalName, setProvisionalName] = useDraftState(draft, 'provisionalName', '')
  const [provisionalEmail, setProvisionalEmail] = useDraftState(draft, 'provisionalEmail', '')
  const [provisionalPhone, setProvisionalPhone] = useDraftState(draft, 'provisionalPhone', '')
  const [identityNote, setIdentityNote] = useState<string | null>(null)
  const [customerCandidateId, setCustomerCandidateId] = useState('')
  const [source, setSource] = useDraftState<InquirySource>(draft, 'source', 'WHATSAPP')
  const [sourceUrl, setSourceUrl] = useDraftState(draft, 'sourceUrl', '')
  const [sourceNotes, setSourceNotes] = useDraftState(draft, 'sourceNotes', '')
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState<string | null>(null)
  const [sourceUploadedUrl, setSourceUploadedUrl] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [selectedServiceTypes, setSelectedServiceTypes] = useDraftState<HelmServiceType[]>(
    draft,
    'selectedServiceTypes',
    ['END_TO_END_IMPORT'],
  )
  const [status, setStatus] = useDraftState<HelmInquiryStatus>(draft, 'status', 'SUBMITTED')
  const [priority, setPriority] = useDraftState<HelmInquiryPriority>(draft, 'priority', 'NORMAL')
  const [channel, setChannel] = useDraftState<HelmCommunicationChannel>(draft, 'channel', 'EMAIL')
  const [shippingMethod, setShippingMethod] = useDraftState<ShippingMethod>(
    draft,
    'shippingMethod',
    'NONE',
  )
  // For air only: freight, express, courier or a chartered aircraft.
  const [airService, setAirService] = useDraftState<HelmAirService | null>(
    draft,
    'airService',
    null,
  )
  const [origin, setOrigin] = useDraftState<LocationChoice | null>(draft, 'origin', CHINA)
  const [destination, setDestination] = useDraftState<LocationChoice | null>(
    draft,
    'destination',
    KAMPALA,
  )
  const [incoterms, setIncoterms] = useDraftState(draft, 'incoterms', '')
  const [urgency, setUrgency] = useDraftState(draft, 'urgency', '')
  const [slaDueAt, setSlaDueAt] = useDraftState(draft, 'slaDueAt', '')
  const [budget, setBudget] = useDraftState(draft, 'budget', '')
  const [declaredValue, setDeclaredValue] = useDraftState(draft, 'declaredValue', '')
  const [supplierName, setSupplierName] = useDraftState(draft, 'supplierName', '')
  const [supplierEmail, setSupplierEmail] = useDraftState(draft, 'supplierEmail', '')
  const [supplierPhone, setSupplierPhone] = useDraftState(draft, 'supplierPhone', '')
  const [supplierAddress, setSupplierAddress] = useDraftState(draft, 'supplierAddress', '')
  const [nextAction, setNextAction] = useDraftState(
    draft,
    'nextAction',
    defaultInquiryNextAction('SUBMITTED'),
  )
  const [notes, setNotes] = useDraftState(draft, 'notes', '')
  const [products, setProducts] = useDraftState<ProductDraft[]>(draft, 'products', [blankProduct()])
  const [submitting, setSubmitting] = useState(false)
  const [parsingSource, setParsingSource] = useState(false)
  const [extractError, setExtractError] = useState<string | null>(null)
  const [extracted, setExtracted] = useState<string[] | null>(null)
  const [unmatchedLead, setUnmatchedLead] = useDraftState<string | null>(
    draft,
    'unmatchedLead',
    null,
  )
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    setSourceUploadedUrl(null)
    if (!sourceFile) {
      setSourcePreviewUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(sourceFile)
    setSourcePreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [sourceFile])

  const filledProducts = products.filter((product) => product.name.trim())
  const hasSourceCapture = Boolean(sourceFile || sourceUrl.trim() || sourceNotes.trim())
  const selectedCustomer = customers?.find((customer) => customer.id === customerId) ?? null
  const customerCandidate =
    customers?.find((customer) => customer.id === customerCandidateId) ?? null

  const supplierDetails = useMemo(() => {
    const supplier = {
      name: supplierName.trim() || null,
      email: supplierEmail.trim() || null,
      phone: supplierPhone.trim() || null,
      address: supplierAddress.trim() || null,
    }
    const hasSupplier = Boolean(
      supplier.name || supplier.email || supplier.phone || supplier.address,
    )

    return {
      ...supplier,
      supplier_options: hasSupplier
        ? [
            {
              id: 'initial-supplier',
              ...supplier,
              quote_status: 'PROSPECT',
              selected: false,
            },
          ]
        : [],
    }
  }, [supplierAddress, supplierEmail, supplierName, supplierPhone])

  function validate(id: StepId): FieldErrors {
    const errors: FieldErrors = {}
    if (id === 'source') {
      if (sourceUrl.trim() && !isLink(sourceUrl)) {
        errors['source-url'] = 'Enter the full link, starting with https://'
      }
    }
    if (id === 'client') {
      if (customerMode === 'existing' && !selectedCustomer)
        errors.customer = 'Choose the existing customer this inquiry is for.'
      if (customerMode === 'provisional') {
        if (!provisionalName.trim())
          errors['provisional-name'] =
            'Enter a name or description to identify this provisional customer.'
        if (provisionalEmail.trim() && !isEmail(provisionalEmail))
          errors['provisional-email'] = 'Enter a valid email address, or leave it blank.'
        if (provisionalPhone.trim() && !/^\+[1-9]\d{6,14}$/.test(provisionalPhone.trim()))
          errors['provisional-phone'] =
            'Use a complete international number such as +256700000001, or leave it blank.'
      }
      if (!selectedServiceTypes.length) errors['service-types'] = 'Choose at least one service.'
    }
    if (id === 'products') {
      if (!filledProducts.length && !hasSourceCapture) {
        errors['product-0-name'] =
          'Name at least one product, or go back and capture the source of the request.'
      }
      products.forEach((product, index) => {
        if (product.name.trim() && !(product.quantity >= 1)) {
          errors[`product-${index}-quantity`] = 'Quantity must be at least 1.'
        }
        if (product.unit_price && !(Number(product.unit_price) >= 0)) {
          errors[`product-${index}-price`] = 'Enter a price of 0 or more.'
        }
        if (product.sourcing_url.trim() && !isLink(product.sourcing_url)) {
          errors[`product-${index}-link`] = 'Enter the full link, starting with https://'
        }
      })
    }
    if (id === 'shipping') {
      if (supplierEmail.trim() && !isEmail(supplierEmail)) {
        errors['supplier-email'] = 'Enter a valid email address.'
      }
    }
    return errors
  }

  const wizard = useFormWizard(steps, validate)
  const errors = wizard.errors

  function handleStatusChange(nextStatus: HelmInquiryStatus) {
    setStatus(nextStatus)
    setNextAction(defaultInquiryNextAction(nextStatus))
  }

  function updateProduct(index: number, patch: Partial<ProductDraft>) {
    setProducts((current) =>
      current.map((product, productIndex) =>
        productIndex === index ? { ...product, ...patch } : product,
      ),
    )
  }

  function removeProduct(index: number) {
    setProducts((current) =>
      current.length === 1
        ? current
        : current.filter((_product, productIndex) => productIndex !== index),
    )
  }

  function toggleServiceType(service: HelmServiceType, checked: boolean) {
    setSelectedServiceTypes((current) => {
      if (checked) {
        return current.includes(service) ? current : [...current, service]
      }

      return current.filter((value) => value !== service)
    })
  }

  function attachSource(file: File | null | undefined) {
    if (!file) return
    if (!ACCEPTED_SOURCE.test(file.type)) {
      setExtractError('Use an image or a PDF.')
      return
    }
    setExtractError(null)
    setExtracted(null)
    setSourceFile(file)
  }

  function handleSourcePaste(event: ClipboardEvent) {
    const file = Array.from(event.clipboardData.files).find((item) =>
      ACCEPTED_SOURCE.test(item.type),
    )
    if (file) {
      event.preventDefault()
      attachSource(file)
    }
  }

  function handleSourceDrop(event: DragEvent) {
    event.preventDefault()
    setDragging(false)
    attachSource(event.dataTransfer.files?.[0])
  }

  async function getSourceUploadUrl() {
    if (!sourceFile) return sourceUrl.trim()
    if (sourceUploadedUrl) return sourceUploadedUrl
    const fileUrl = await uploadInvoiceAsset(sourceFile)
    setSourceUploadedUrl(fileUrl)
    return fileUrl
  }

  function applyParsedServiceTypes(values: string[] | undefined) {
    const allowed = new Set(serviceTypes)
    const parsed = (values ?? []).filter((value): value is HelmServiceType =>
      allowed.has(value as HelmServiceType),
    )
    if (parsed.length > 0) {
      setSelectedServiceTypes(parsed)
    }
    return parsed.length > 0
  }

  function matchCustomerFromParsed(
    parsed: Awaited<ReturnType<typeof apiClient.parseInquirySource>>,
  ) {
    return customerFromExactEmail(customers ?? [], parsed.email)
  }

  async function handleParseSource() {
    setExtractError(null)
    setExtracted(null)
    setIdentityNote(null)
    setCustomerCandidateId('')
    if (!sourceFile && !sourceUrl.trim()) {
      setExtractError('Add a screenshot or a link first.')
      return
    }

    setParsingSource(true)
    try {
      const fileUrl = await getSourceUploadUrl()
      const parsed = await apiClient.parseInquirySource(fileUrl, source)
      const filled: string[] = []
      const matchedCustomer = matchCustomerFromParsed(parsed)
      if (matchedCustomer) {
        setCustomerCandidateId(matchedCustomer.id)
        setIdentityNote(
          `Possible existing customer: ${customerIdentityLabel(matchedCustomer)}. The email matches, but other source details may conflict. Review all identity details before selecting; your customer selection has not changed.`,
        )
      } else if (
        parsed.company_name ||
        parsed.customer_name ||
        parsed.contact_name ||
        parsed.email ||
        parsed.phone
      ) {
        setIdentityNote(
          'The extracted identity needs review. Choose an existing customer or enter a provisional identity; names alone are not an automatic match.',
        )
      }

      if (applyParsedServiceTypes(parsed.service_types)) filled.push('services')
      if (
        parsed.preferred_communication_channel &&
        channels.includes(parsed.preferred_communication_channel as HelmCommunicationChannel)
      ) {
        setChannel(parsed.preferred_communication_channel as HelmCommunicationChannel)
      }
      if (parsed.priority && priorities.includes(parsed.priority as HelmInquiryPriority)) {
        setPriority(parsed.priority as HelmInquiryPriority)
      }
      if (parsed.shipping_method && ['AIR', 'SEA', 'ROAD'].includes(parsed.shipping_method)) {
        setShippingMethod(parsed.shipping_method as 'AIR' | 'SEA' | 'ROAD')
        filled.push(`${parsed.shipping_method.toLowerCase()} shipping`)
      }
      if (parsed.origin_country || parsed.destination_address) {
        const mode = (
          ['AIR', 'SEA', 'ROAD'].includes(parsed.shipping_method ?? '')
            ? parsed.shipping_method
            : shippingMethod
        ) as ShippingMethod
        const places = await loadLocations().catch(() => null)
        const place = (text: string, role: 'origin' | 'destination') =>
          places ? resolveLocation(places, text, role, mode) : typedChoice(text)
        if (parsed.origin_country) setOrigin(place(parsed.origin_country, 'origin'))
        if (parsed.destination_address)
          setDestination(place(parsed.destination_address, 'destination'))
      }
      if (parsed.incoterms) setIncoterms(incotermFromText(parsed.incoterms))
      if (parsed.urgency) setUrgency(parsed.urgency)
      if (parsed.supplier_details?.name) setSupplierName(parsed.supplier_details.name)
      if (parsed.supplier_details?.email) setSupplierEmail(parsed.supplier_details.email)
      if (parsed.supplier_details?.phone) setSupplierPhone(parsed.supplier_details.phone)
      if (parsed.supplier_details?.address) setSupplierAddress(parsed.supplier_details.address)
      if (parsed.supplier_details?.name || parsed.supplier_details?.email) filled.push('supplier')
      if (parsed.budget != null) setBudget(String(parsed.budget))
      if (parsed.declared_value != null) setDeclaredValue(String(parsed.declared_value))
      if (parsed.products?.length) {
        setProducts(
          parsed.products.map((product) => ({
            name: product.name || '',
            description: product.description || '',
            quantity: product.quantity && product.quantity > 0 ? product.quantity : 1,
            unit_price: product.unit_price != null ? String(product.unit_price) : '',
            currency: product.currency || 'USD',
            sourcing_url: product.sourcing_url || '',
            hs_code: product.hs_code || '',
            notes: product.notes || '',
          })),
        )
        filled.push(
          parsed.products.length === 1 ? '1 product' : `${parsed.products.length} products`,
        )
      }

      const lead = [
        parsed.company_name || parsed.customer_name,
        parsed.contact_name,
        parsed.email,
        parsed.phone,
      ]
        .filter(Boolean)
        .join(' · ')
      setUnmatchedLead(lead || null)

      const extractedNotes = [
        parsed.notes,
        lead ? `Extracted customer lead for staff review: ${lead}` : null,
        parsed.confidence != null
          ? `AI extraction confidence: ${Math.round(parsed.confidence * 100)}%`
          : null,
      ]
        .filter(Boolean)
        .join('\n')
      if (extractedNotes) {
        setSourceNotes((current) => [current.trim(), extractedNotes].filter(Boolean).join('\n\n'))
      }
      setExtracted(filled)
    } catch (error) {
      setExtractError(
        error instanceof Error
          ? error.message
          : 'Helm could not read the source. Fill it in below.',
      )
    } finally {
      setParsingSource(false)
    }
  }

  async function handleSubmit() {
    setSubmitError(null)

    const sourceLines = [
      `Inquiry source: ${formatLabel(source)}`,
      sourceUrl.trim() ? `Source URL: ${sourceUrl.trim()}` : null,
      sourceFile ? `Source screenshot: ${sourceFile.name}` : null,
      sourceNotes.trim() ? `Source notes: ${sourceNotes.trim()}` : null,
    ].filter(Boolean)
    const inquiryNotes = [notes.trim(), sourceLines.length ? sourceLines.join('\n') : null]
      .filter(Boolean)
      .join('\n\n')
    const productList =
      filledProducts.length > 0
        ? filledProducts.map((product) => ({
            name: product.name.trim(),
            description: product.description.trim() || null,
            quantity: product.quantity,
            unit_price: product.unit_price ? Number(product.unit_price) : null,
            currency: product.currency.trim() || 'USD',
            sourcing_url: product.sourcing_url.trim() || null,
            hs_code: product.hs_code.trim() || null,
            notes: product.notes.trim() || null,
          }))
        : [
            {
              name: `${formatLabel(source)} inquiry`,
              description:
                sourceNotes.trim() ||
                sourceUrl.trim() ||
                sourceFile?.name ||
                'Source capture pending qualification',
              quantity: 1,
              unit_price: null,
              currency: 'USD',
              sourcing_url: sourceUrl.trim() || null,
              hs_code: null,
              notes: sourceFile ? `Source screenshot: ${sourceFile.name}` : null,
            },
          ]

    setSubmitting(true)
    let documents: InquiryDocumentArtifact[] = []
    try {
      if (sourceFile) {
        const fileUrl = await getSourceUploadUrl()
        documents = [
          {
            type: 'INQUIRY_SOURCE_SCREENSHOT',
            file_url: fileUrl,
            file_name: sourceFile.name,
            uploaded_at: new Date().toISOString(),
            source_channel: source,
            source_url: sourceUrl.trim() || null,
          },
        ]
      }
    } catch (error) {
      setSubmitting(false)
      setSubmitError(error instanceof Error ? error.message : 'Failed to upload source screenshot')
      return
    }

    const payload: CreateInquiryRequest = {
      ...(customerMode === 'existing'
        ? { customer_id: customerId }
        : {
            customer: {
              name: provisionalName.trim(),
              ...(provisionalEmail.trim() ? { email: provisionalEmail.trim() } : {}),
              ...(provisionalPhone.trim() ? { phone: provisionalPhone.trim() } : {}),
            },
          }),
      source,
      service_type: selectedServiceTypes[0],
      service_types: selectedServiceTypes,
      status,
      ...(customerLogin ? {} : { priority }),
      preferred_communication_channel: channel,
      next_action: nextAction.trim() || null,
      origin_country: origin?.country ?? (origin?.label || null),
      origin_location_code: origin?.code ?? null,
      destination_address: destination?.label || null,
      destination_location_code: destination?.code ?? null,
      shipping_method: shippingMethod === 'NONE' ? null : shippingMethod,
      air_service: shippingMethod === 'AIR' ? airService : null,
      incoterms: incoterms.trim() || null,
      urgency: urgency.trim() || null,
      ...(customerLogin ? {} : { sla_due_at: slaDueAt ? new Date(slaDueAt).toISOString() : null }),
      budget_cents: centsFromInput(budget),
      declared_value_cents: centsFromInput(declaredValue),
      supplier_details: supplierDetails,
      notes: inquiryNotes || null,
      product_list: productList,
      documents,
    }

    try {
      const inquiry = await apiClient.createInquiry(payload)
      draft.clear()
      navigate(`/inquiries/${inquiry.id}`)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create inquiry')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <LoadingState label="Loading customers..." />
  if (error) return <ErrorState error={error} onRetry={reload} />

  const disabled = submitting

  return (
    <div className="space-y-6">
      <PageHeader
        title="New Inquiry"
        description="Capture the request before it becomes an operational consignment."
        breadcrumbs={[{ label: 'Inquiries', href: '/inquiries' }, { label: 'New Inquiry' }]}
        actions={
          <Button type="button" variant="outline" asChild>
            <Link to="/inquiries">
              <X className="h-4 w-4" />
              Cancel
            </Link>
          </Button>
        }
      />

      <FormWizard
        wizard={wizard}
        submitLabel="Create inquiry"
        busy={submitting}
        busyLabel="Creating inquiry"
        error={submitError}
        errorTitle="The inquiry was not created"
        onSubmit={handleSubmit}
        draft={draft}
        draftLabel="inquiry answers"
      >
        {wizard.step.id === 'source' && (
          <FieldGroup onPaste={handleSourcePaste}>
            <Field>
              <HintedLabel htmlFor="source" hint={FIELD_HELP.inquirySource}>
                Where did it come from?
              </HintedLabel>
              <EnumSelect
                value={source}
                onValueChange={(value) => setSource(value as InquirySource)}
                options={options(inquirySources)}
                disabled={disabled}
                triggerProps={{ id: 'source' }}
              />
            </Field>

            <Field data-tour="inquiry-source">
              <HintedLabel htmlFor="source-screenshot" hint={FIELD_HELP.sourceScreenshot}>
                Screenshot or document
              </HintedLabel>
              {sourceFile ? (
                <div className="flex items-center gap-3 rounded-md border p-2">
                  {sourcePreviewUrl && sourceFile.type.startsWith('image/') ? (
                    <img
                      src={sourcePreviewUrl}
                      alt="Inquiry source screenshot"
                      className="h-16 w-24 rounded object-cover"
                    />
                  ) : (
                    <FileText className="mx-4 size-6 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm">{sourceFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove the screenshot"
                    onClick={() => {
                      setSourceFile(null)
                      setExtracted(null)
                    }}
                    disabled={disabled}
                  >
                    <X />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="source-screenshot"
                  onDragOver={(event) => {
                    event.preventDefault()
                    setDragging(true)
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleSourceDrop}
                  className={`flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground hover:bg-accent ${dragging ? 'border-primary bg-primary/5' : ''}`}
                >
                  <ImagePlus className="size-5" />
                  <span>Drop, paste or choose a screenshot of the request</span>
                  <span className="text-xs">An image or a PDF</span>
                </label>
              )}
              <input
                id="source-screenshot"
                type="file"
                accept="image/*,.pdf"
                className="sr-only"
                onChange={(event) => {
                  attachSource(event.target.files?.[0])
                  event.target.value = ''
                }}
                disabled={disabled}
              />
            </Field>

            <TextField
              id="source-url"
              label="Link"
              value={sourceUrl}
              onChange={setSourceUrl}
              error={errors['source-url']}
              disabled={disabled}
              placeholder="https://..."
              inputMode="url"
              description="A post, listing or message the request points to."
            />

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleParseSource}
                disabled={disabled || parsingSource || (!sourceFile && !sourceUrl.trim())}
              >
                {parsingSource ? <Loader2 className="animate-spin" /> : <Sparkles />}
                {parsingSource ? 'Reading the source' : 'Fill in from the source'}
              </Button>
              <span className="text-xs text-muted-foreground">
                Helm reads the screenshot or link and fills in the next steps for you to check.
              </span>
            </div>

            {extracted && (
              <Alert role="status">
                <CheckCircle2 />
                <AlertDescription>
                  {extracted.length
                    ? `Filled in ${extracted.join(', ')}. Check them in the next steps.`
                    : 'Nothing could be filled in from this source. Fill in the next steps yourself.'}
                </AlertDescription>
              </Alert>
            )}
            {extractError && (
              <Alert variant="destructive">
                <AlertDescription>{extractError}</AlertDescription>
              </Alert>
            )}

            <TextAreaField
              id="source-notes"
              label="Message or notes"
              value={sourceNotes}
              onChange={setSourceNotes}
              disabled={disabled}
              rows={4}
              placeholder="Paste the client's message, or note what they asked for."
            />
          </FieldGroup>
        )}

        {wizard.step.id === 'client' && (
          <FieldGroup>
            <Field>
              <HintedLabel htmlFor="customer-mode" hint={FIELD_HELP.customerIdentity}>
                Customer identity
              </HintedLabel>
              <EnumSelect
                value={customerMode}
                onValueChange={(value) => setCustomerMode(value as 'existing' | 'provisional')}
                options={[
                  { value: 'existing', label: 'Existing customer' },
                  { value: 'provisional', label: 'Identity not confirmed' },
                ]}
                disabled={disabled}
                triggerProps={{ id: 'customer-mode' }}
              />
            </Field>
            {identityNote && (
              <Alert>
                <AlertDescription>{identityNote}</AlertDescription>
              </Alert>
            )}
            {customerCandidate && (
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-sm">Candidate: {customerIdentityLabel(customerCandidate)}</p>
                {unmatchedLead && (
                  <p className="text-sm text-muted-foreground">
                    Extracted source identity for review: {unmatchedLead}
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  disabled={disabled}
                  onClick={() => {
                    setCustomerMode('existing')
                    setCustomerId(customerCandidate.id)
                    setIdentityNote(
                      `You selected ${customerIdentityLabel(customerCandidate)}. Extracted contact details remain source context only.`,
                    )
                    setCustomerCandidateId('')
                  }}
                >
                  Use this customer
                </Button>
              </div>
            )}
            {customerMode === 'existing' ? (
              <Field data-invalid={Boolean(errors.customer)} data-tour="inquiry-client">
                <FieldLabel htmlFor="customer" required>
                  Client
                </FieldLabel>
                <CustomerCombobox
                  id="customer"
                  customers={customers ?? []}
                  value={customerId}
                  onValueChange={setCustomerId}
                  disabled={disabled || !customers?.length}
                  placeholder="Search by name, contact or customer ID"
                  aria-invalid={Boolean(errors.customer)}
                  aria-describedby={errors.customer ? 'customer-error' : undefined}
                />
                {unmatchedLead && !customerId && (
                  <FieldDescription>
                    Source identity for review: {unmatchedLead}. Search existing records first. If
                    the identity is not confirmed, choose the provisional option above.
                  </FieldDescription>
                )}
                {selectedCustomer && (
                  <FieldDescription>
                    {selectedCustomer.customer_number ||
                      'Helm assigns this existing customer an ID when the inquiry is saved.'}
                    {selectedCustomer.is_placeholder
                      ? ' · Provisional identity — complete the profile after verification.'
                      : ''}
                  </FieldDescription>
                )}
                {!customers?.length && (
                  <FieldDescription>
                    No active customers are available. You can capture an unconfirmed identity using
                    the provisional option.
                  </FieldDescription>
                )}
                <FieldError id="customer-error">{errors.customer}</FieldError>
              </Field>
            ) : (
              <FieldGroup data-tour="inquiry-client">
                <Alert>
                  <AlertDescription>
                    Helm will create a provisional customer with a permanent customer ID unless the
                    supplied identity uniquely matches an existing record. Staff can review a later
                    association. No existing customer is merged and no extracted contact is copied
                    automatically.
                  </AlertDescription>
                </Alert>
                <TextField
                  id="provisional-name"
                  label="Provisional name"
                  required
                  value={provisionalName}
                  onChange={setProvisionalName}
                  error={errors['provisional-name']}
                  disabled={disabled}
                  maxLength={255}
                  description="Use the name supplied by the requester, or a clear description if their name is unknown."
                />
                <TextField
                  id="provisional-email"
                  label="Email, if known"
                  type="email"
                  value={provisionalEmail}
                  onChange={setProvisionalEmail}
                  error={errors['provisional-email']}
                  disabled={disabled}
                  maxLength={255}
                />
                <TextField
                  id="provisional-phone"
                  label="Phone, if known"
                  type="tel"
                  value={provisionalPhone}
                  onChange={setProvisionalPhone}
                  error={errors['provisional-phone']}
                  disabled={disabled}
                  maxLength={60}
                  description="Use the international country code. Leave unknown contact details empty."
                />
                {unmatchedLead && (
                  <FieldDescription>
                    Extracted source identity for staff review only: {unmatchedLead}
                  </FieldDescription>
                )}
              </FieldGroup>
            )}

            <FieldSet
              id="service-types"
              data-tour="inquiry-services"
              tabIndex={-1}
              data-invalid={Boolean(errors['service-types'])}
              aria-describedby={errors['service-types'] ? 'service-types-error' : undefined}
              className="outline-none"
            >
              <FieldLegend variant="label" className="flex items-center gap-1.5">
                <span>
                  Services<span className="text-destructive"> *</span>
                </span>
                <FieldHelp label="What are Services for?">{FIELD_HELP.services}</FieldHelp>
              </FieldLegend>
              <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-2">
                {serviceTypes.map((option) => (
                  <label
                    key={option}
                    className="flex min-h-9 items-center gap-2 rounded-sm px-2 text-sm hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedServiceTypes.includes(option)}
                      disabled={disabled}
                      onCheckedChange={(checked) => toggleServiceType(option, checked === true)}
                    />
                    <span>{formatLabel(option)}</span>
                  </label>
                ))}
              </div>
              <FieldError id="service-types-error">{errors['service-types']}</FieldError>
            </FieldSet>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <HintedLabel htmlFor="channel" hint={FIELD_HELP.reachBy}>
                  Reach them by
                </HintedLabel>
                <EnumSelect
                  value={channel}
                  onValueChange={(value) => setChannel(value as HelmCommunicationChannel)}
                  options={options(channels)}
                  disabled={disabled}
                  triggerProps={{ id: 'channel' }}
                />
              </Field>
              {!customerLogin && (
                <Field>
                  <HintedLabel htmlFor="priority" hint={FIELD_HELP.priority}>
                    Priority
                  </HintedLabel>
                  <EnumSelect
                    value={priority}
                    onValueChange={(value) => setPriority(value as HelmInquiryPriority)}
                    options={options(priorities)}
                    disabled={disabled}
                    triggerProps={{ id: 'priority' }}
                  />
                </Field>
              )}
            </div>
          </FieldGroup>
        )}

        {wizard.step.id === 'products' && (
          <FieldGroup>
            {hasSourceCapture && !filledProducts.length && (
              <p className="text-sm text-muted-foreground">
                No products yet. That's fine: the inquiry keeps the source you captured, and
                products can be added once the request is clear.
              </p>
            )}
            {products.map((product, index) => (
              <FieldSet
                key={index}
                className="rounded-md border p-4"
                data-tour={index === 0 ? 'inquiry-products' : undefined}
              >
                <div className="flex items-center justify-between gap-3">
                  <FieldLegend variant="label" className="mb-0">
                    Product {index + 1}
                  </FieldLegend>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeProduct(index)}
                    disabled={disabled || products.length === 1}
                    aria-label={`Remove product ${index + 1}`}
                  >
                    <Trash2 />
                  </Button>
                </div>
                <div className="grid gap-4 md:grid-cols-4">
                  <TextField
                    id={`product-${index}-name`}
                    label="Name"
                    className="md:col-span-2"
                    value={product.name}
                    onChange={(value) => updateProduct(index, { name: value })}
                    error={errors[`product-${index}-name`]}
                    disabled={disabled}
                  />
                  <TextField
                    id={`product-${index}-quantity`}
                    label="Quantity"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    value={product.quantity ? String(product.quantity) : ''}
                    onChange={(value) => updateProduct(index, { quantity: Number(value) })}
                    error={errors[`product-${index}-quantity`]}
                    disabled={disabled}
                  />
                  <TextField
                    id={`product-${index}-price`}
                    label={`Unit price (${product.currency || 'USD'})`}
                    hint={FIELD_HELP.unitPrice}
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={product.unit_price}
                    onChange={(value) => updateProduct(index, { unit_price: value })}
                    error={errors[`product-${index}-price`]}
                    disabled={disabled}
                  />
                  <TextAreaField
                    id={`product-${index}-description`}
                    label="Description"
                    className="md:col-span-2"
                    rows={2}
                    value={product.description}
                    onChange={(value) => updateProduct(index, { description: value })}
                    disabled={disabled}
                  />
                  <TextField
                    id={`product-${index}-hs`}
                    label="HS code"
                    hint={FIELD_HELP.hsCode}
                    value={product.hs_code}
                    onChange={(value) => updateProduct(index, { hs_code: value })}
                    disabled={disabled}
                  />
                  <TextField
                    id={`product-${index}-currency`}
                    label="Currency"
                    hint={FIELD_HELP.currency}
                    value={product.currency}
                    onChange={(value) => updateProduct(index, { currency: value.toUpperCase() })}
                    disabled={disabled}
                    maxLength={3}
                  />
                  <TextField
                    id={`product-${index}-link`}
                    label="Where to buy it"
                    hint={FIELD_HELP.sourcingUrl}
                    className="md:col-span-2"
                    value={product.sourcing_url}
                    onChange={(value) => updateProduct(index, { sourcing_url: value })}
                    error={errors[`product-${index}-link`]}
                    disabled={disabled}
                    placeholder="https://..."
                    inputMode="url"
                  />
                  <TextField
                    id={`product-${index}-notes`}
                    label="Notes"
                    className="md:col-span-2"
                    value={product.notes}
                    onChange={(value) => updateProduct(index, { notes: value })}
                    disabled={disabled}
                  />
                </div>
              </FieldSet>
            ))}

            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setProducts((current) => [...current, blankProduct()])}
                disabled={disabled}
              >
                <PlusCircle />
                Add product
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="budget"
                label="Client's budget"
                hint={FIELD_HELP.budget}
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={budget}
                onChange={setBudget}
                disabled={disabled}
              />
              <TextField
                id="declared-value"
                label="Declared value"
                hint={FIELD_HELP.declaredValue}
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={declaredValue}
                onChange={setDeclaredValue}
                disabled={disabled}
              />
            </div>
          </FieldGroup>
        )}

        {wizard.step.id === 'shipping' && (
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-2" data-tour="inquiry-shipping">
              <Field>
                <HintedLabel htmlFor="shipping-method" hint={FIELD_HELP.shipmentMode}>
                  Shipment mode
                </HintedLabel>
                <EnumSelect
                  value={shipmentChoice(shippingMethod, airService)}
                  onValueChange={(value) => {
                    const fields = shipmentFields(value as ShipmentChoice)
                    setShippingMethod(fields.shipping_method ?? 'NONE')
                    setAirService(fields.air_service)
                  }}
                  options={SHIPMENT_CHOICES}
                  disabled={disabled}
                  triggerProps={{ id: 'shipping-method' }}
                />
              </Field>
              <Field>
                <HintedLabel htmlFor="incoterms" hint={FIELD_HELP.incoterms}>
                  Incoterms
                </HintedLabel>
                <IncotermSelect
                  id="incoterms"
                  mode={shippingMethod}
                  value={incoterms}
                  onChange={setIncoterms}
                  disabled={disabled}
                />
                {incotermModeHint(incoterms, shippingMethod) && (
                  <FieldDescription>{incotermModeHint(incoterms, shippingMethod)}</FieldDescription>
                )}
              </Field>
              <Field>
                <HintedLabel htmlFor="origin" hint={FIELD_HELP.origin}>
                  Origin
                </HintedLabel>
                <LocationPicker
                  id="origin"
                  aria-label="Origin"
                  role="origin"
                  mode={shippingMethod}
                  value={origin}
                  onChange={setOrigin}
                  disabled={disabled}
                />
                {locationModeHint(origin, 'origin', shippingMethod) && (
                  <FieldDescription>
                    {locationModeHint(origin, 'origin', shippingMethod)}
                  </FieldDescription>
                )}
              </Field>
              <Field>
                <HintedLabel htmlFor="destination" hint={FIELD_HELP.destination}>
                  Destination
                </HintedLabel>
                <LocationPicker
                  id="destination"
                  aria-label="Destination"
                  role="destination"
                  mode={shippingMethod}
                  value={destination}
                  onChange={setDestination}
                  disabled={disabled}
                />
                {locationModeHint(destination, 'destination', shippingMethod) && (
                  <FieldDescription>
                    {locationModeHint(destination, 'destination', shippingMethod)}
                  </FieldDescription>
                )}
              </Field>
            </div>
            <FieldSet>
              <FieldLegend variant="label">Supplier</FieldLegend>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id="supplier-name"
                  label="Name"
                  value={supplierName}
                  onChange={setSupplierName}
                  disabled={disabled}
                />
                <TextField
                  id="supplier-email"
                  label="Email"
                  type="email"
                  inputMode="email"
                  value={supplierEmail}
                  onChange={setSupplierEmail}
                  error={errors['supplier-email']}
                  disabled={disabled}
                />
                <TextField
                  id="supplier-phone"
                  label="Phone"
                  type="tel"
                  value={supplierPhone}
                  onChange={setSupplierPhone}
                  disabled={disabled}
                />
                <TextField
                  id="supplier-address"
                  label="Address"
                  value={supplierAddress}
                  onChange={setSupplierAddress}
                  disabled={disabled}
                />
              </div>
            </FieldSet>
          </FieldGroup>
        )}

        {wizard.step.id === 'review' && (
          <div className="space-y-6">
            <FieldSet data-tour="inquiry-follow-up">
              <FieldLegend variant="label">Follow-up</FieldLegend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field>
                  <HintedLabel htmlFor="status" hint={FIELD_HELP.inquiryStatus}>
                    Status
                  </HintedLabel>
                  <EnumSelect
                    value={status}
                    onValueChange={(value) => handleStatusChange(value as HelmInquiryStatus)}
                    options={options(statuses)}
                    disabled={disabled}
                    triggerProps={{ id: 'status' }}
                  />
                </Field>
                <Field>
                  <HintedLabel htmlFor="next-action" hint={FIELD_HELP.nextAction}>
                    Next action
                  </HintedLabel>
                  <EnumSelect
                    value={nextAction}
                    onValueChange={setNextAction}
                    options={inquiryNextActionsForStatus(status).map((option) => ({
                      value: option.value,
                      label: option.value,
                    }))}
                    disabled={disabled}
                    triggerProps={{ id: 'next-action' }}
                  />
                </Field>
                {!customerLogin && (
                  <TextField
                    id="due-date"
                    label="Reply due by"
                    hint={FIELD_HELP.replyDue}
                    type="datetime-local"
                    value={slaDueAt}
                    onChange={setSlaDueAt}
                    disabled={disabled}
                  />
                )}
                <TextField
                  id="urgency"
                  label="Urgency"
                  hint={FIELD_HELP.urgency}
                  value={urgency}
                  onChange={setUrgency}
                  disabled={disabled}
                  placeholder="Needed before shipment cutoff"
                />
                <TextAreaField
                  id="notes"
                  label="Internal notes"
                  className="sm:col-span-2"
                  value={notes}
                  onChange={setNotes}
                  disabled={disabled}
                  rows={3}
                />
              </div>
            </FieldSet>

            <div className="space-y-3">
              <ReviewSection title="Source" onEdit={() => wizard.goToStep('source')}>
                <ReviewRow label="From">{formatLabel(source)}</ReviewRow>
                <ReviewRow label="Screenshot">{sourceFile?.name}</ReviewRow>
                <ReviewRow label="Link">{sourceUrl.trim()}</ReviewRow>
                <ReviewRow label="Message">{sourceNotes.trim().split('\n')[0]}</ReviewRow>
              </ReviewSection>
              <ReviewSection title="Client & services" onEdit={() => wizard.goToStep('client')}>
                <ReviewRow label="Client">
                  {customerMode === 'provisional'
                    ? provisionalName.trim()
                    : selectedCustomer
                      ? customerIdentityLabel(selectedCustomer)
                      : undefined}
                </ReviewRow>
                <ReviewRow label="Customer identity">
                  {customerMode === 'provisional'
                    ? 'Provisional; server assigns or reuses the customer ID on save'
                    : selectedCustomer?.is_placeholder
                      ? 'Existing provisional customer'
                      : 'Existing customer'}
                </ReviewRow>
                {customerMode === 'provisional' && (
                  <>
                    <ReviewRow label="Email supplied">
                      {provisionalEmail.trim() || 'Unknown'}
                    </ReviewRow>
                    <ReviewRow label="Phone supplied">
                      {provisionalPhone.trim() || 'Unknown'}
                    </ReviewRow>
                  </>
                )}
                <ReviewRow label="Services">
                  {selectedServiceTypes.map(formatLabel).join(', ')}
                </ReviewRow>
                <ReviewRow label="Reach them by">{formatLabel(channel)}</ReviewRow>
                {!customerLogin && <ReviewRow label="Priority">{formatLabel(priority)}</ReviewRow>}
              </ReviewSection>
              <ReviewSection title="Products" onEdit={() => wizard.goToStep('products')}>
                {filledProducts.length ? (
                  filledProducts.map((product, index) => (
                    <ReviewRow key={index} label={`Product ${index + 1}`}>
                      {product.name.trim()} × {product.quantity}
                      {product.unit_price
                        ? ` at ${product.currency || 'USD'} ${product.unit_price}`
                        : ''}
                    </ReviewRow>
                  ))
                ) : (
                  <ReviewRow label="Products">
                    {hasSourceCapture ? 'From the source, to be confirmed' : undefined}
                  </ReviewRow>
                )}
                <ReviewRow label="Budget">{budget}</ReviewRow>
                <ReviewRow label="Declared value">{declaredValue}</ReviewRow>
              </ReviewSection>
              <ReviewSection title="Shipping & supplier" onEdit={() => wizard.goToStep('shipping')}>
                <ReviewRow label="Mode">
                  {
                    SHIPMENT_CHOICES.find(
                      (option) => option.value === shipmentChoice(shippingMethod, airService),
                    )?.label
                  }
                </ReviewRow>
                <ReviewRow label="Route">
                  {[origin?.label, destination?.label].filter(Boolean).join(' → ')}
                </ReviewRow>
                <ReviewRow label="Incoterms">{incoterms.trim()}</ReviewRow>
                <ReviewRow label="Supplier">
                  {[supplierName.trim(), supplierEmail.trim(), supplierPhone.trim()]
                    .filter(Boolean)
                    .join(' · ')}
                </ReviewRow>
              </ReviewSection>
            </div>
          </div>
        )}
      </FormWizard>
    </div>
  )
}
